import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { noop } from 'lodash-es';
import { FieldPath, FieldValues } from 'react-hook-form';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { FDMap } from '../../../util/data-structure/FDMap';
import { createForm, IFormField } from '../../../view-models/form.view-model';
import { selectedListOverviewFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { UnstyledFieldset } from '../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import { Panel } from './Panel';
import { useCompanyEditPerms } from './useEditPerms';
import { useCompanyListsFields } from './useCompanyListsFields';

export const CompanyDetailsPanelBreakpoint = '380px';

export const FieldsWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(4, minmax(4rem, auto));
  gap: 0 2rem;
  min-height: 320px;
  @container fd-panel (width < ${CompanyDetailsPanelBreakpoint}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    min-height: 0;
  }
`;

export const FieldsPerPage = 8;

export interface IProfilePanelProps<T extends FieldValues> {
  companyId: number;
  fieldsToShow?: FieldPath<T>[];
  editable?: boolean;
  defaultPage?: number;
}

export function CompanyListsOverviewPanel({
  companyId,
  fieldsToShow,
  editable = true,
  defaultPage,
}: IProfilePanelProps<ICompanyViewModel>) {
  const selectedFields = useAtomValue(selectedListOverviewFieldsOrgState);
  const setEditableFields = useCompanyEditPerms(companyId);

  const companyDetailsFields = useCompanyListsFields(fieldsToShow ?? selectedFields);

  const fields = useMemo(
    () => setEditableFields(companyDetailsFields),
    [companyDetailsFields, setEditableFields]
  );

  return (
    <Panel title={'Company Overview'} showContentOverflow={true} defaultPage={defaultPage}>
      <UnstyledFieldset disabled={!editable}>
        <CompanyListsOverviewPage fields={fields} companyId={companyId} />
      </UnstyledFieldset>
    </Panel>
  );
}

interface ICompanyListsOverviewProps {
  fields: IFormField<unknown>[];
  companyId: number;
}

function CompanyListsOverviewPage(props: ICompanyListsOverviewProps) {
  const { fields } = props;

  const form = createForm<ICompanyViewModel>({
    fields: FDMap.fromArray(fields, 'key'),
    variant: 'form-inline',
  });

  return (
    <FieldsWrapper>
      <FormFactoryNoLayout form={form} onChange={noop} />
    </FieldsWrapper>
  );
}
